import { ProcedureInstance } from "@/backend/models/procedureInstances/procedureInstance";
import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { ParameterType } from "@/types/parameterTypes";
import { useEffect, useState } from "react";
import { BasicButton } from "../buttons/BasicButton";
import { SideNav } from "../sideNav/SideNav";
import { Link } from "react-router-dom";
import { ProcedureParameterRun } from "../parameters/ProcedureParameterRun";
import { SectionRun } from "../containers/SectionRun";
import { scrollToTop } from "@/frontend/utils/scrollTo";
import { ProcedureTabsNavigation } from "./ProcedureTabsNavigation";
import { IntroductionAsset } from "../parameters/IntroductionAsset";
import { EquipmentTable } from "../equipment/EquipmentTable";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { ProcedurePlaceholder } from "../containers/ContainersPlaceholder";

export function ProcedureRun() {
  const procedure = useProcedureInstanceStore((state) => state.procedureInstance) as ProcedureInstance;
  const builtProcedure = new ProcedureInstance(procedure);
  const sections = useTreeStore((state) => state.rootContainerNodes);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (procedure.signature) {
      disableInputs();
    }
  });

  function disableInputs() {
    const disableArea = document.getElementById('disable-container');
    disableArea?.querySelectorAll('input, select, textarea').forEach((element) => {
      element.setAttribute('disabled', 'true');
    });
    disableArea?.querySelectorAll('.hide-on-publish').forEach((element) => {
      element.classList.add('d-none');
    });
  }

  const link = procedure.isTemplate() ? '/procedure-templates' : '/procedures';
  const linkText = procedure.isTemplate() ? I18n.get('templates') : I18n.get('procedures');

  const parts = useProjectStore(state => state.projectParts);
  const fetchPartsForProcedureInstance = useProjectStore(state => state.fetchPartsForProcedureInstance);
  useEffect(() => {
    setLoading(true);
    void fetchPartsForProcedureInstance(procedure.id).finally(() => {
      setLoading(false);
    });
  }, [procedure.id]);

  if (loading) {
    return <ProcedurePlaceholder />;
  }

  return (
    <div className="w-100 d-flex h-100" style={{position: 'relative', maxHeight: '100vh', height: '100vh'}} data-bs-spy="scroll" data-bs-target="#navbarScroller" data-bs-offset="0">
      <div className='mh-100'>
        <SideNav rootName={procedure.title}  link={<Link to={link}>{linkText}</Link>} />
      </div>
      <div className="flex-fill w-100 d-flex flex-column gap-2 overflow-scroll">
          <ProcedureTabsNavigation />
          <div id='disable-container' className='overflow-scroll mh-100 flex-fill px-2 d-flex flex-column gap-2'>
            <h3 className='max-w-100'>{procedure.title}</h3>
            { (procedure?.description || procedure?.estimatedRunTime) && (
              <div className='card'>
                <h5 className='card-header'>{I18n.get('generalInformation')}</h5>
                <div className='card-body d-flex flex-column gap-1'>
                  {procedure?.description && <p className='text-break p-2' style={{ whiteSpace: 'pre-wrap' }}>{I18n.get('procedureDescription')}: {procedure.description}</p>}
                  {procedure?.estimatedRunTime && <p className='text-break p-2'>{I18n.get('estimatedRunTime')}: {procedure.estimatedRunTime}</p>}
                </div>
              </div>
            )}
            <IntroductionAsset editView={false} />
            { parts.length > 0 && (
              <div className='card'>
                <div className='card-header'>{I18n.get('deviceUnderTest')}</div>
                <div className='card-body'>
                  { parts.map(part => {
                    return (
                      <div key={part.id}>
                        <div>{part.title}</div>
                        { part.description && <div className='text-muted'>{part.description}</div> }
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <EquipmentTable />
            {!!procedure.signature && <p className='text-danger d-print-none'>{builtProcedure.isTemplate() ? I18n.get('runViewTemplatePublishedWarning') : I18n.get('runViewPublishedWarning')}</p>}
            <ProcedureParameterRun id={procedure.id} parameterType={procedure.isTemplate() ? ParameterType.TEST_INPUT : ParameterType.CONFIG} />
            <ProcedureParameterRun id={procedure.id} parameterType={ParameterType.INPUT} />
            <ul className="list-group gap-2 mt-3">
              { sections.map(section => <SectionRun key={`procedure-run-${section.id}`} section={section} />) }
            </ul>
          {/* <ProcedureSignature /> */}
          <div className="mb-3 text-center">
            <BasicButton className="btn btn-sm d-print-none" onClick={scrollToTop} label={I18n.get('backToTop')} />
          </div>
        </div>
      </div>
    </div>
  );
}
